import { CCError } from "./errors";

export const SR = 16000;
export const SILENCE_RMS = 0.004;

export function rms(a, s = 0, e = a.length) {
  const n = e - s;
  if (n <= 0) return 0;
  const step = Math.max(1, Math.floor(n / 40000));
  let sum = 0;
  let c = 0;
  for (let i = s; i < e; i += step) {
    sum += a[i] * a[i];
    c++;
  }
  return Math.sqrt(sum / c);
}

async function toMono16k(ab) {
  if (ab.sampleRate === SR && ab.numberOfChannels === 1) return ab.getChannelData(0);
  const len = Math.max(1, Math.ceil(ab.duration * SR));
  const off = new OfflineAudioContext(1, len, SR);
  const src = off.createBufferSource();
  src.buffer = ab;
  src.connect(off.destination);
  src.start();
  const r = await off.startRendering();
  return r.getChannelData(0);
}

async function decodeWithWebAudio(file) {
  const buf = await file.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  let ctx;
  try { ctx = new Ctx({ sampleRate: SR }); } catch (e) { ctx = new Ctx(); }
  try {
    const ab = await ctx.decodeAudioData(buf);
    return await toMono16k(ab);
  } finally {
    ctx.close && ctx.close();
  }
}

async function decodeWithFFmpeg(file) {
  const { FFmpeg } = await import(/* webpackIgnore: true */ `${window.location.origin}${process.env.PUBLIC_URL || ""}/ffmpeg/index.js`);
  const ff = new FFmpeg();
  let log = "";
  ff.on("log", ({ message }) => { log += message + "\n"; });
  const base = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/esm";
  const toBlobURL = async (url, type) => {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`HTTP ${r.status} for ${url}`);
    return URL.createObjectURL(new Blob([await r.arrayBuffer()], { type }));
  };
  try {
    await ff.load({
      coreURL: await toBlobURL(`${base}/ffmpeg-core.js`, "text/javascript"),
      wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, "application/wasm"),
    });
  } catch (e) {
    throw new CCError("NETWORK", "Could not load FFmpeg.wasm for audio extraction: " + (e?.message || String(e)));
  }
  await ff.writeFile("in", new Uint8Array(await file.arrayBuffer()));
  await ff.exec(["-i", "in", "-vn", "-ac", "1", "-ar", String(SR), "-f", "f32le", "out.pcm"]);
  let data;
  try {
    data = await ff.readFile("out.pcm");
  } catch (e) {
    data = null;
  }
  ff.terminate();
  if (!data || !data.length) {
    if (/does not contain any stream|matches no streams|Output file is empty|no audio/i.test(log) || /Stream #.*Video/.test(log)) {
      throw new CCError("NO_AUDIO", "No audio track found in this video.");
    }
    throw new CCError("DECODE", "FFmpeg could not decode the audio of this file.");
  }
  const copy = new Uint8Array(data);
  return new Float32Array(copy.buffer, 0, Math.floor(copy.byteLength / 4));
}

export async function extractAudio(file, onFallback) {
  try {
    return await decodeWithWebAudio(file);
  } catch (e) {
    if (e instanceof RangeError) throw new CCError("OOM", "Not enough memory to decode this video's audio.");
    onFallback && onFallback();
    return await decodeWithFFmpeg(file);
  }
}

// Split audio into <=maxSec chunks, cutting at the quietest point near each boundary.
export function makeChunks(audio, maxSec = 28) {
  const chunks = [];
  const N = audio.length;
  const win = SR / 10;
  let s = 0;
  while (s < N) {
    let e = Math.min(N, s + maxSec * SR);
    if (e < N) {
      let best = e;
      let bestR = Infinity;
      for (let p = e - 5 * SR; p + win <= e; p += win / 2) {
        const r = rms(audio, p, p + win);
        if (r < bestR) { bestR = r; best = p + win / 2; }
      }
      e = Math.floor(best);
    }
    chunks.push({ s, e, start: s / SR, end: e / SR });
    s = e;
  }
  return chunks;
}
